import { useFocusEffect } from "expo-router";
import { COLORS } from "../lib/brand";
import { useCallback, useRef, useState } from "react";
import { Platform, Pressable, Text, View, type View as RNView } from "react-native";
import Svg, { Path } from "react-native-svg";

import { getUnreadCount } from "../lib/notifications";
import { NotificationDropdown } from "./NotificationDropdown";

const FOREST = COLORS.FOREST.hex;
const AMBER = COLORS.AMBER.hex;
const LINEN = COLORS.LINEN.hex;

type Props = {
  theme?: "light" | "dark";
  /** `icon` = 32x32 header control with a tinted bell glyph and amber unread dot. */
  layout?: "boxed" | "icon";
  /**
   * Where the dropdown's "Open inbox" footer routes. Each portal shell passes
   * its own portal-scoped inbox path (`/student/notifications`,
   * `/client/notifications`, etc.) so users always land in the right shell.
   * Defaults to legacy `/notifications` which itself role-redirects.
   */
  inboxHref?: string;
};

function BellGlyph({ color, size = 18 }: { color: string; size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M6 8a6 6 0 1 1 12 0c0 5.6 1.8 7.4 2.6 8H3.4C4.2 15.4 6 13.6 6 8Z"
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M10.3 20a1.9 1.9 0 0 0 3.4 0"
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/**
 * Bell icon in the topbar. Tap opens a floating dropdown of recent activity;
 * the dropdown's footer routes to `/notifications` (the full inbox).
 */
export function NotificationBell({
  theme = "light",
  layout = "boxed",
  inboxHref = "/notifications",
}: Props) {
  const [unread, setUnread] = useState(0);
  const [webHover, setWebHover] = useState(false);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; right: number }>({ top: 56, right: 20 });
  const wrapperRef = useRef<RNView | null>(null);

  const refresh = useCallback(async () => {
    const n = await getUnreadCount();
    setUnread(n);
  }, []);

  useFocusEffect(
    useCallback(() => {
      void refresh();
    }, [refresh])
  );

  const webMouse =
    Platform.OS === "web"
      ? ({
          onMouseEnter: () => setWebHover(true),
          onMouseLeave: () => setWebHover(false),
        } as const)
      : {};

  function openDropdown() {
    if (Platform.OS === "web" && wrapperRef.current) {
      // Compute anchor from bell's current viewport rect.
      const node = wrapperRef.current as unknown as { getBoundingClientRect?: () => DOMRect };
      const rect = node.getBoundingClientRect?.();
      if (rect) {
        const winW = typeof window !== "undefined" ? window.innerWidth : 1200;
        setAnchor({
          top: Math.round(rect.bottom + 8),
          right: Math.max(12, Math.round(winW - rect.right)),
        });
      }
    }
    setOpen(true);
  }

  function closeDropdown() {
    setOpen(false);
    void refresh();
  }

  const baseTint = theme === "dark" ? LINEN : FOREST;
  const tint = Platform.OS === "web" && webHover ? AMBER : baseTint;

  if (layout === "icon") {
    return (
      <View ref={wrapperRef} collapsable={false}>
        <Pressable
          {...webMouse}
          onPress={openDropdown}
          accessibilityRole="button"
          accessibilityLabel="Notifications"
          style={[
            { width: 32, height: 32, alignItems: "center", justifyContent: "center" },
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {},
          ]}
        >
          <View
            className="relative"
            style={{ width: 22, height: 22, alignItems: "center", justifyContent: "center" }}
          >
            <BellGlyph color={tint} size={20} />
            {unread > 0 ? (
              <View
                className="absolute h-2 w-2 rounded-full"
                style={{
                  top: 0,
                  right: 0,
                  backgroundColor: AMBER,
                  borderWidth: 1,
                  borderColor: theme === "dark" ? COLORS.BLACK.hex : COLORS.SAND.hex,
                }}
              />
            ) : null}
          </View>
        </Pressable>

        <NotificationDropdown
          visible={open}
          anchorRight={anchor.right}
          anchorTop={anchor.top}
          onClose={closeDropdown}
          onUnreadChange={(n) => setUnread(n)}
          inboxHref={inboxHref}
        />
      </View>
    );
  }

  // Boxed layout (compact header pill).
  const border =
    theme === "dark" ? "border-white/15 bg-white/5" : "border-sand-dark bg-white/60";
  return (
    <View ref={wrapperRef} collapsable={false}>
      <Pressable
        {...webMouse}
        className={`rounded-btn border px-2.5 py-2 ${border}`}
        style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}
        onPress={openDropdown}
        accessibilityRole="button"
        accessibilityLabel="Notifications"
      >
        <View
          className="relative flex-row items-center justify-center"
          style={{ width: 18, height: 18 }}
        >
          <BellGlyph color={tint} size={18} />
          {unread > 0 ? (
            <View
              className="absolute -right-1.5 -top-1.5 min-h-[16px] min-w-[16px] items-center justify-center rounded-full px-1"
              style={{
                backgroundColor: AMBER,
                borderWidth: 1,
                borderColor: theme === "dark" ? COLORS.BLACK.hex : COLORS.SAND.hex,
              }}
            >
              <Text
                className="text-[9px] font-bold"
                style={{ fontFamily: "DMSans_700Bold", color: FOREST }}
              >
                {unread > 99 ? "99+" : unread}
              </Text>
            </View>
          ) : null}
        </View>
      </Pressable>

      <NotificationDropdown
        visible={open}
        anchorRight={anchor.right}
        anchorTop={anchor.top}
        onClose={closeDropdown}
        onUnreadChange={(n) => setUnread(n)}
      />
    </View>
  );
}
